import './index.scss';

// import '@/mui/license';
import React from 'react';
import { Provider } from 'react-redux';
import { HashRouter as Router } from 'react-router';
import dayjs from 'dayjs';
import type { EmotionCache } from '@emotion/cache';
import { CacheProvider } from '@emotion/react';

import {
  REACT_APP_BACKUP_PLAYLIST_ORIGIN,
  REACT_APP_BUILD_DATE_TIME,
  REACT_APP_BUILD_ENV,
  REACT_APP_CONFIG_BUCKET,
  REACT_APP_CONFIG_ENV,
  REACT_APP_IRE_PLAYER_WIDGET_ORIGIN,
  REACT_APP_IRE_PLAYER_WIDGET_PART_URL,
  REACT_APP_SOCIAL_SHARING_ORIGIN,
  REACT_APP_TINYMCE_KEY,
  REACT_APP_TRAFFIC_MANAGER_ORIGIN,
  REACT_APP_VERSION,
} from './modules/common/constants/envs';
import { REACT_APP_MAIN_SCRIPT_TRIGGER } from './modules/common/constants/globals';

import { IsInPopupContext, RootContainers } from './modules/common/helpers';
import { DOMContentLoaded, handlePublisherAnchor } from './modules/common/helpers/dom';
import { getContainerId, setExternalEmbedData } from './modules/common/helpers/embedCodeParams';
import logger from './modules/common/helpers/logger';
import {
  isInSameDomain,
  isWatchInIframe,
  tryPresetInIframeRouter,
  tryPresetSocialRouter,
} from './modules/common/helpers/routes';
import store from './modules/common/helpers/store';
import { setStoreForRequestFn } from '@/modules/common/helpers/request';
import { configFetchStartAction } from '@/modules/common/redux/slices/common';

import App from './modules/App';
import ScrollObserverProvider from './modules/common/components/ScrollObserver/ScrollObserverProvider';
import GlobalTrigger from './modules/GlobalTrigger';
import { SnackbarGroup } from '@/mui/components';

window[REACT_APP_MAIN_SCRIPT_TRIGGER] = async () => {
  setExternalEmbedData();

  await DOMContentLoaded();

  const containerId = getContainerId();
  const parentContainer = document.getElementById(containerId);

  logger.log(
    `App version => ${REACT_APP_VERSION}\n`,
    `App Build Time => ${dayjs(REACT_APP_BUILD_DATE_TIME).format('MMM D, YYYY hh:mm A')}\n`,
    `Parent Container Id => ${containerId}\n`,
    `Parent Container DOM Element => ${parentContainer}\n`,
    `Build environment => ${REACT_APP_BUILD_ENV}\n`,
    `Config environment => ${REACT_APP_CONFIG_ENV}\n`,
    `Backup Playlist Origin => ${REACT_APP_BACKUP_PLAYLIST_ORIGIN}\n`,
    `Tinymce Public Key => ${REACT_APP_TINYMCE_KEY}\n`,
    `PLayer Widget Origin => ${REACT_APP_IRE_PLAYER_WIDGET_ORIGIN}\n`,
    `PLayer Widget Path => ${REACT_APP_IRE_PLAYER_WIDGET_PART_URL}\n`,
    `TM Origin => ${REACT_APP_TRAFFIC_MANAGER_ORIGIN}\n`,
    `Config Bucket => ${REACT_APP_CONFIG_BUCKET}\n`,
    `Social Sharing Origin => ${REACT_APP_SOCIAL_SHARING_ORIGIN}\n`,
    `Watch In The Iframe => ${isWatchInIframe}\n`,
    `Same Domain Iframe => ${isInSameDomain}\n`,
  );

  if (!parentContainer) {
    return;
  }

  document.body.style.margin = '0';

  handlePublisherAnchor();
  setStoreForRequestFn(store);

  tryPresetSocialRouter();
  if (isWatchInIframe && isInSameDomain) {
    tryPresetInIframeRouter();
  }

  store.dispatch(configFetchStartAction());

  RootContainers.render((cache: EmotionCache) => (
    <Provider store={store}>
      <Router>
        <CacheProvider value={cache}>
          <IsInPopupContext.Provider value={false}>
            <GlobalTrigger>
              <ScrollObserverProvider>
                <App />
                <SnackbarGroup maxSnack={4} />
              </ScrollObserverProvider>
            </GlobalTrigger>
          </IsInPopupContext.Provider>
        </CacheProvider>
      </Router>
    </Provider>
  ));
};
// DO NOT TOUCH, this was created to have a possibility twice module execution
window[REACT_APP_MAIN_SCRIPT_TRIGGER]();
